// PR-P0-5 reference: Policies (Settings, not UC-numbered) · ADR-0008 · docs/feature-status-and-gaps.md §2.7 (adjacent)
// Mockup: NONE — Policies UI landed pre-UC-numbering; see nav-flow-mock/ for placement
// Systems Architecture: §5.13
import { defineTemplate, derive } from '../egress/data-classes.js';
import { listBindingSummariesForItem, readExpectedEvidenceTypes, } from './evidence-service.js';
import { sqlIn } from '../utils/sql-in.js';
import { sqlNow } from '../utils/sql-dialect.js';
// ---- Local RNG ------------------------------------------------------------
function cryptoRandom() {
    const g = globalThis;
    if (typeof g === 'object' &&
        g !== null &&
        'crypto' in g &&
        typeof g.crypto?.randomUUID ===
            'function') {
        return g.crypto
            .randomUUID()
            .replace(/-/g, '')
            .slice(0, 16);
    }
    return Math.random().toString(36).slice(2, 18);
}
// ---- Section catalog ------------------------------------------------------
// Order matches how the final document reads top-to-bottom. Numbering
// is applied at render time (skipping level-gated kinds keeps numbers
// contiguous).
const SECTION_ORDER = [
    'document_control',
    'purpose',
    'scope',
    'definitions',
    'policy_statement',
    'roles_responsibilities',
    'procedures',
    'monitoring_metrics',
    'enforcement',
    'review_revision',
    'related_documents',
    'coverage_matrix',
    'register_index',
];
// Minimum target level at which each section is included in the doc.
// Sections at their gate level are still included with a lighter body;
// deeper sections stay omitted below their gate.
const LEVEL_GATE = {
    document_control: 2,
    purpose: 2,
    scope: 2,
    definitions: 2,
    policy_statement: 2,
    roles_responsibilities: 2,
    procedures: 3,
    monitoring_metrics: 4,
    enforcement: 2,
    review_revision: 2,
    related_documents: 2,
    coverage_matrix: 2,
    register_index: 2,
};
const SECTION_TITLE = {
    document_control: 'Document Control',
    purpose: 'Purpose',
    scope: 'Scope',
    definitions: 'Definitions',
    policy_statement: 'Policy Statement',
    roles_responsibilities: 'Roles & Responsibilities',
    procedures: 'Procedures',
    monitoring_metrics: 'Monitoring & Metrics',
    enforcement: 'Enforcement & Exceptions',
    review_revision: 'Review & Revision',
    related_documents: 'Related Documents',
    coverage_matrix: 'Appendix A — Coverage Matrix',
    register_index: 'Appendix B — Register Items in Scope',
};
// Which evidence types back each chapter. Used to decide grounded vs
// template AND to pick which artifact_ids the LLM prompt cites.
const SECTION_EVIDENCE_TYPES = {
    document_control: [],
    purpose: [],
    scope: [],
    definitions: [],
    policy_statement: ['policy'],
    roles_responsibilities: [],
    procedures: ['process', 'technical'],
    monitoring_metrics: ['monitoring'],
    enforcement: [],
    review_revision: [],
    related_documents: ['policy', 'technical', 'process', 'monitoring'],
    coverage_matrix: [], // synthesised, always grounded
    register_index: [], // synthesised from the register + framework catalog
};
// Sections we render deterministically — no LLM call needed.
const DETERMINISTIC_SECTIONS = new Set([
    'document_control',
    'coverage_matrix',
    'register_index',
]);
function isSectionGroundedExpanded(kind, ctx) {
    if (kind === 'coverage_matrix' || kind === 'register_index')
        return true;
    if (kind === 'document_control')
        return true;
    switch (kind) {
        // Grounded when register items exist — we know the exact risks,
        // categories, severity, and framework drivers.
        case 'purpose':
        case 'scope':
        case 'definitions':
        case 'enforcement':
            return ctx.hasItems;
        // Grounded when register items exist (the items ARE the control
        // objectives), OR when evidence artifacts back them.
        case 'policy_statement':
            return ctx.hasItems || ctx.bundleTypes.has('policy');
        // Grounded when we know the workspace's team roles.
        case 'roles_responsibilities':
            return ctx.hasTeamRoles || ctx.hasItems;
        // These still require evidence artifacts — procedures and monitoring
        // need actual SOPs/dashboards to cite substantively.
        case 'procedures':
            return ['process', 'technical'].some((t) => ctx.bundleTypes.has(t));
        case 'monitoring_metrics':
            return ctx.bundleTypes.has('monitoring') || ctx.hasAssessment;
        // Review cadence is deterministic from target level — always grounded.
        case 'review_revision':
            return ctx.hasItems;
        // Related documents still need evidence artifacts to list.
        case 'related_documents': {
            const evTypes = SECTION_EVIDENCE_TYPES[kind];
            return evTypes.some((t) => ctx.bundleTypes.has(t));
        }
        default:
            return false;
    }
}
// Attestation cadence hint by level (matches evidence-service constants).
const CADENCE_MONTHS_BY_LEVEL = {
    2: 12,
    3: 6,
    4: 3,
    5: 3,
};
// ---- Category-specific policy domain context --------------------------------
// Layer 2 of the drafter enrichment: domain expertise the LLM uses to write
// substantive, category-aware policy prose instead of generic templates.
// Each entry describes the key requirements, standard practices, and
// regulatory expectations specific to that risk category.
const CATEGORY_POLICY_CONTEXT = {
    governance: [
        'This category covers organizational AI governance structures.',
        'Key requirements: (1) Establish a named AI governance body (e.g. AI Governance Council, AI Ethics Board) with a charter that defines authority, composition, meeting cadence, and escalation paths.',
        '(2) Define risk appetite and tolerance thresholds for AI systems by classification tier.',
        '(3) Require documented AI strategy alignment — every AI initiative must trace to a board-approved strategic objective.',
        '(4) Mandate periodic (at least annual) governance effectiveness reviews with findings reported to the board or senior leadership.',
        'Standard practices: RACI matrices for governance roles, AI risk registers reviewed quarterly, governance KPIs (time-to-decision, policy coverage ratio, exception rate).',
        'Regulatory context: NIST AI RMF GOVERN function (GV-1 through GV-6), ISO 42001 Clause 5 (Leadership), EU AI Act Article 9 (Risk Management System).',
    ].join(' '),
    access_control: [
        'This category covers identity, authentication, authorization, and privilege management for AI systems and their data.',
        'Key requirements: (1) Enforce least-privilege access — every identity (human and service) is granted only the minimum permissions needed for its function.',
        '(2) Require multi-factor authentication for all privileged access to AI training environments, model registries, and inference endpoints.',
        '(3) Implement segregation of duties — no single individual can both approve a model for production and deploy it.',
        '(4) Conduct periodic access reviews: quarterly for privileged accounts, semi-annually for standard accounts.',
        '(5) Implement automated provisioning/deprovisioning tied to HR lifecycle events (onboarding, role change, termination).',
        'Standard practices: role-based access control (RBAC), just-in-time privileged access, session recording for administrative actions, service account inventory with ownership.',
        'Regulatory context: FedRAMP AC family (AC-2 Account Management, AC-3 Access Enforcement, AC-6 Least Privilege), NIST AI RMF MAP-1.5, ISO 42001 A.6.',
    ].join(' '),
    audit_logging: [
        'This category covers system activity recording, log integrity, and audit trail completeness for AI operations.',
        'Key requirements: (1) Log all security-relevant events: authentication attempts, authorization decisions, model access, training data access, inference requests to high-risk systems, and configuration changes.',
        '(2) Ensure log integrity — logs must be tamper-evident (append-only storage, cryptographic hashing, or WORM media).',
        '(3) Define retention periods by log type: security logs ≥ 1 year, operational logs ≥ 90 days, compliance-relevant logs per regulatory requirement.',
        '(4) Implement centralized log aggregation with automated alerting for anomalous patterns.',
        '(5) Ensure audit logs capture sufficient context: who, what, when, where, outcome, and the AI system identifier.',
        'Standard practices: SIEM integration, log correlation rules, periodic log completeness audits, response playbooks for log-derived alerts.',
        'Regulatory context: FedRAMP AU family (AU-2 Auditable Events, AU-3 Content of Audit Records, AU-6 Audit Review), SOC 2 CC7.2, ISO 42001 A.8.',
    ].join(' '),
    incident_response: [
        'This category covers detection, escalation, containment, and recovery for AI system failures, misuse, and security breaches.',
        'Key requirements: (1) Define AI-specific incident categories: model failure (unexpected output, hallucination at scale), data breach (training data exfiltration), adversarial attack (prompt injection, model poisoning), bias incident (discriminatory output detected in production).',
        '(2) Establish severity tiers (P1–P4) with defined response times: P1 critical ≤ 1 hour acknowledgment, ≤ 4 hours containment.',
        '(3) Require post-incident review (blameless retrospective) within 5 business days for P1/P2 incidents, with findings tracked to remediation.',
        '(4) Mandate notification procedures: internal stakeholders, affected data subjects (per privacy law), regulators (per breach notification requirements).',
        'Standard practices: incident commander rotation, communication templates, tabletop exercises quarterly, runbook library per AI system type.',
        'Regulatory context: NIST AI RMF MANAGE-4 (incident response), EU AI Act Article 62 (reporting obligations), FedRAMP IR family, SOC 2 CC7.3–CC7.5.',
    ].join(' '),
    change_management: [
        'This category covers version control, deployment approval, rollback, and impact assessment for AI model and system changes.',
        'Key requirements: (1) All changes to production AI systems (model updates, retraining, hyperparameter tuning, pipeline modifications) must pass through a documented change approval process.',
        '(2) Implement staged deployment: development → staging/validation → canary/shadow → production, with defined quality gates at each stage.',
        '(3) Require rollback capability — every production deployment must have a documented rollback procedure tested within the last [cadence] cycle.',
        '(4) Conduct impact assessments for significant changes: changes affecting model behavior, data sources, or system classification require a written impact assessment reviewed by the policy owner before deployment.',
        '(5) Maintain a change log with: change description, requestor, approver, risk assessment outcome, deployment date, and rollback plan reference.',
        'Standard practices: automated CI/CD with policy-as-code gates, model versioning with provenance tracking, A/B testing frameworks, change advisory board for high-risk changes.',
        'Regulatory context: FedRAMP CM family (CM-3 Configuration Change Control, CM-4 Security Impact Analysis), NIST AI RMF MANAGE-2, ISO 42001 A.7.',
    ].join(' '),
    data_governance: [
        'This category covers data quality, lineage, classification, retention, and lifecycle management for AI training and inference data.',
        'Key requirements: (1) Classify all data used in AI systems by sensitivity tier (public, internal, confidential, restricted) and by AI-specific role (training data, validation data, test data, inference input, inference output).',
        '(2) Maintain documented data lineage — every dataset used in model training must trace to its source, transformation history, and quality assessment.',
        '(3) Implement data quality controls: completeness checks, distribution drift monitoring, bias proxy detection, and freshness validation.',
        '(4) Define retention and deletion policies per data category, with documented legal holds capability.',
        '(5) Require data impact assessments before introducing new data sources into AI training pipelines.',
        'Standard practices: data catalogs with ownership, automated data quality scoring, synthetic data governance, data sharing agreements for cross-boundary flows.',
        'Regulatory context: NIST AI RMF MAP-2 (data requirements), ISO 42001 A.3 (Data for AI Systems), EU AI Act Article 10 (Data and Data Governance), FedRAMP SI-12.',
    ].join(' '),
    privacy: [
        'This category covers personal data protection, consent, data subject rights, and privacy impact assessments in AI processing.',
        'Key requirements: (1) Conduct a Privacy Impact Assessment (PIA) or Data Protection Impact Assessment (DPIA) before deploying any AI system that processes personal data.',
        '(2) Implement lawful basis documentation — each AI processing activity must have a documented legal basis (consent, legitimate interest, contractual necessity, etc.).',
        '(3) Support data subject rights: access, rectification, erasure, portability, and the right to object to automated decision-making.',
        '(4) Implement data minimization — AI systems must collect and retain only the personal data necessary for the stated purpose.',
        '(5) Apply privacy-by-design: pseudonymization, differential privacy, or federated learning where feasible; document the privacy-preserving measures applied.',
        'Standard practices: Records of Processing Activities (ROPA), consent management platforms, anonymization validation testing, privacy-preserving ML techniques inventory.',
        'Regulatory context: GDPR Articles 22, 35, 36; CCPA/CPRA; HIPAA where applicable; NIST AI RMF MAP-1.6; ISO 42001 A.4.',
    ].join(' '),
    security: [
        'This category covers technical safeguards, vulnerability management, penetration testing, and secure development for AI infrastructure.',
        'Key requirements: (1) Apply secure development lifecycle (SDLC) practices to all AI system development: threat modeling, secure code review, dependency scanning, and pre-deployment security testing.',
        '(2) Conduct vulnerability management: automated scanning of AI infrastructure weekly, remediation SLAs (critical ≤ 24h, high ≤ 7d, medium ≤ 30d).',
        '(3) Perform AI-specific security testing: adversarial robustness testing, prompt injection resistance, model extraction resistance, and data poisoning detection.',
        '(4) Implement network segmentation — AI training environments, model serving infrastructure, and data stores must be isolated from general-purpose networks.',
        '(5) Encrypt data at rest (AES-256 or equivalent) and in transit (TLS 1.2+) for all AI system data flows.',
        'Standard practices: OWASP ML Top 10 assessments, red team exercises for AI systems, supply chain security for ML dependencies (SBOM), secrets management for API keys and model access tokens.',
        'Regulatory context: FedRAMP SC/SI families, NIST AI RMF MANAGE-3, ISO 42001 A.5, SOC 2 CC6.',
    ].join(' '),
    fairness_bias: [
        'This category covers bias detection, demographic parity, outcome equity testing, and mitigation for AI decision systems.',
        'Key requirements: (1) Conduct bias impact assessments before deploying any AI system that affects individuals — assess across protected characteristics (race, gender, age, disability, etc.).',
        '(2) Define fairness metrics appropriate to the use case: demographic parity, equalized odds, calibration, or individual fairness — document the choice and rationale.',
        '(3) Implement ongoing bias monitoring in production: measure fairness metrics on live data at defined intervals (at least monthly for high-risk systems).',
        '(4) Define remediation thresholds — if a fairness metric deviates beyond the acceptable range, trigger a documented review and remediation process within [timeframe].',
        '(5) Maintain documentation of training data composition: demographic representation, known gaps, and steps taken to address imbalances.',
        'Standard practices: pre-deployment fairness audits, disaggregated performance reporting, bias bounty programs, diverse evaluation datasets, fairness testing as a CI/CD gate.',
        'Regulatory context: NIST AI RMF MAP-2.3 (bias), EU AI Act Article 10(2)(f), ISO 42001 A.9 (Fairness), NYC Local Law 144 (automated employment decisions).',
    ].join(' '),
    transparency: [
        'This category covers explainability, model documentation, decision rationale disclosure, and stakeholder communication about AI capabilities.',
        'Key requirements: (1) Produce model cards or AI system documentation for every production AI system: purpose, intended use, limitations, performance metrics, training data summary, and known failure modes.',
        '(2) Provide decision explanations to affected individuals when AI systems make or materially influence significant decisions — explanations must be understandable to non-technical stakeholders.',
        '(3) Maintain public-facing AI transparency disclosures: what AI systems the organization operates, their general purpose, and how individuals can contest decisions.',
        '(4) Document known limitations and failure modes — no AI system is deployed without a written limitations statement reviewed by the policy owner.',
        'Standard practices: model cards (per Mitchell et al.), datasheets for datasets (per Gebru et al.), LIME/SHAP explanations for tabular models, attention visualization for NLP, system behavior documentation for generative AI.',
        'Regulatory context: EU AI Act Articles 13–14 (Transparency, Human Oversight), NIST AI RMF GOVERN-1.4, ISO 42001 A.10 (Transparency), CCPA right to know about automated decision-making.',
    ].join(' '),
    safety: [
        'This category covers operational safety boundaries, human override controls, failure mode analysis, and harm prevention for AI systems.',
        'Key requirements: (1) Conduct pre-deployment safety assessments: identify potential harms (physical, psychological, financial, reputational), likelihood, and severity for each AI system.',
        '(2) Implement human-in-the-loop or human-on-the-loop controls for high-risk AI decisions — define which decisions require human approval before execution.',
        '(3) Design kill switches and graceful degradation: every production AI system must have a documented mechanism to disable AI functionality and revert to manual/rule-based operation.',
        '(4) Conduct failure mode and effects analysis (FMEA) for critical AI systems — document failure scenarios, their impact, and mitigation measures.',
        '(5) Implement output guardrails: range checks, confidence thresholds, anomaly detection on AI outputs before they reach end users or downstream systems.',
        'Standard practices: safety cases, red-team evaluations, canary deployments with rollback triggers, continuous output monitoring, simulated failure drills.',
        'Regulatory context: EU AI Act Articles 9, 14 (Human Oversight), NIST AI RMF MEASURE function, ISO 42001 A.11 (Safety), OECD AI Principle 1.4 (Robustness, Security, Safety).',
    ].join(' '),
    supply_chain: [
        'This category covers third-party AI component vetting, vendor risk assessment, model provenance, and dependency management.',
        'Key requirements: (1) Assess all third-party AI components before adoption: foundation models, pre-trained models, training datasets, ML libraries, and AI-as-a-service APIs.',
        '(2) Maintain a vendor AI inventory: every third-party AI component in use, its vendor, version, license, data handling practices, and the organizational owner responsible for the relationship.',
        '(3) Require vendor security and compliance assessments: SOC 2 Type II or equivalent, data processing agreements, incident notification commitments, and right-to-audit clauses.',
        '(4) Monitor for supply chain vulnerabilities: dependency scanning for ML libraries, model provenance verification, and tracking of known vulnerabilities in foundation model versions.',
        '(5) Define vendor exit strategies: for each critical AI vendor dependency, document the migration path and the maximum acceptable transition time.',
        'Standard practices: SBOM for ML pipelines, model provenance chains (training data → base model → fine-tune → deployment), vendor tiering (critical/high/standard), contractual AI-specific clauses.',
        'Regulatory context: NIST AI RMF MAP-5 (third-party), EU AI Act Article 28 (obligations of providers and deployers), ISO 42001 A.12, FedRAMP SA-4/SA-9.',
    ].join(' '),
    content_safety: [
        'This category covers output filtering, harmful content detection, guardrails, and moderation policies for generative AI systems.',
        'Key requirements: (1) Define prohibited content categories for each generative AI system: hate speech, harassment, self-harm content, CSAM, disinformation, PII leakage, and domain-specific prohibitions.',
        '(2) Implement layered content filtering: pre-generation input screening, generation-time guardrails, and post-generation output review.',
        '(3) Establish human review workflows for flagged content: define escalation criteria, review SLAs, and feedback loops to improve automated filters.',
        '(4) Monitor content safety metrics: false positive rate, false negative rate (via red-team testing), user reports, and filter bypass attempts.',
        '(5) Conduct periodic red-team testing of content filters: at least quarterly, with findings tracked to remediation.',
        'Standard practices: content classifiers, topic blocklists, output length and confidence limits, watermarking for AI-generated content, user reporting mechanisms.',
        'Regulatory context: EU AI Act Article 52 (transparency for AI-generated content), NIST AI RMF MEASURE-2.6, digital services regulations, platform-specific safety standards.',
    ].join(' '),
    intellectual_property: [
        'This category covers training data licensing, model ownership, output IP rights, and protection against data extraction.',
        'Key requirements: (1) Audit all training data for licensing compliance: verify that every dataset used in model training has appropriate rights (license, consent, fair use analysis).',
        '(2) Define model ownership and IP assignment: document who owns the trained model, the training pipeline, and the generated outputs under applicable employment/contractor agreements.',
        '(3) Implement protections against model extraction and training data memorization: rate limiting on inference APIs, output perturbation for sensitive models, and membership inference testing.',
        '(4) Establish AI-generated content attribution practices: when AI systems produce content used externally, define disclosure and attribution requirements.',
        '(5) Maintain a training data provenance registry: for each production model, record the datasets used, their licenses, and any restrictions on downstream use.',
        'Standard practices: license compliance scanning, copyright risk assessment for training corpora, output ownership clauses in customer agreements, model-as-trade-secret protections.',
        'Regulatory context: EU AI Act Article 53 (copyright compliance for GPAI), US Copyright Office guidance on AI-generated works, NIST AI RMF GOVERN-1.7.',
    ].join(' '),
    environmental: [
        'This category covers energy consumption, carbon footprint reporting, and compute efficiency optimization for AI workloads.',
        'Key requirements: (1) Measure and report the energy consumption of AI training and inference workloads: track GPU-hours, electricity consumption (kWh), and carbon emissions (CO2e).',
        '(2) Evaluate compute efficiency before scaling: document the cost-benefit analysis for large training runs, including environmental impact alongside model performance gains.',
        '(3) Implement compute efficiency practices: model distillation, quantization, pruning, and efficient architecture selection to reduce energy per inference.',
        '(4) Report environmental impact in organizational sustainability disclosures and to relevant regulatory bodies.',
        '(5) Set reduction targets: define year-over-year targets for energy efficiency improvement (e.g. inference energy per request) aligned with organizational sustainability commitments.',
        'Standard practices: carbon accounting for ML (ML CO2 Impact tool), green cloud region selection, batch inference scheduling during off-peak hours, model efficiency benchmarking.',
        'Regulatory context: EU AI Act recitals on environmental sustainability, EU Corporate Sustainability Reporting Directive (CSRD), NIST AI RMF GOVERN-1.5, ISO 42001 A.13.',
    ].join(' '),
};
// ---- Errors ---------------------------------------------------------------
export class PolicyDraftValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'PolicyDraftValidationError';
    }
}
// ---- Service --------------------------------------------------------------
export class PolicyDrafterService {
    db;
    llm;
    constructor(db, 
    // Optional: without the egress gateway (or when it refuses — federal mode
    // with no eligible provider, ADR-0073 parity policy_drafting →
    // deterministic), LLM-drafted sections fall back to deterministic
    // Template placeholders. Document Control + Coverage Matrix always render.
    llm) {
        this.db = db;
        this.llm = llm;
    }
    // ------------------------------------------------------------------
    // Read paths
    // ------------------------------------------------------------------
    async list() {
        const res = await this.db.query(`SELECT * FROM policy_drafts
        WHERE workspace_id = ?
        ORDER BY updated_at DESC`, [this.db.workspaceId]);
        return res.results;
    }
    async get(draftId) {
        const res = await this.db.query(`SELECT * FROM policy_drafts
        WHERE workspace_id = ? AND draft_id = ?
        LIMIT 1`, [this.db.workspaceId, draftId]);
        return res.results[0] ?? null;
    }
    // ------------------------------------------------------------------
    // Scope preview — no writes
    // ------------------------------------------------------------------
    async previewScope(profileId, scope) {
        const items = await this.selectItemsForScope(scope);
        const bundleTypes = await this.bundleTypesForScope(profileId, items);
        const profile = await this.loadProfileForGrounding(profileId);
        const frameworkDetails = await this.loadFrameworkDetailsFor(items);
        const teamSummary = await this.loadTeamSummary();
        const assessmentSummary = await this.loadAssessmentSummary(profileId);
        const groundingCtx = {
            bundleTypes,
            hasItems: items.length > 0,
            hasProfile: profile !== null,
            hasFrameworkDescriptions: frameworkDetails.size > 0,
            hasAssessment: assessmentSummary !== null,
            hasTeamRoles: teamSummary !== null && teamSummary.total_members > 0,
        };
        const emptyLevelMap = () => ({
            2: 0,
            3: 0,
            4: 0,
            5: 0,
        });
        const section_estimate = emptyLevelMap();
        const grounded_estimate = emptyLevelMap();
        for (const level of [2, 3, 4, 5]) {
            for (const kind of SECTION_ORDER) {
                if (LEVEL_GATE[kind] > level)
                    continue;
                section_estimate[level] += 1;
                if (isSectionGroundedExpanded(kind, groundingCtx)) {
                    grounded_estimate[level] += 1;
                }
            }
        }
        return {
            item_ids: items.map((i) => i.item_id),
            count: items.length,
            section_estimate,
            grounded_estimate,
        };
    }
    // ------------------------------------------------------------------
    // Generate
    // ------------------------------------------------------------------
    async generateDraft(input) {
        if (input.workspace_id !== this.db.workspaceId) {
            throw new PolicyDraftValidationError(`workspace_id ${input.workspace_id} does not match scoped db ${this.db.workspaceId}`);
        }
        const title = input.title.trim();
        if (!title)
            throw new PolicyDraftValidationError('title is required');
        if (![2, 3, 4, 5].includes(input.target_maturity_level)) {
            throw new PolicyDraftValidationError(`target_maturity_level must be 2..5, got ${input.target_maturity_level}`);
        }
        const items = await this.selectItemsForScope(input.scope);
        if (items.length === 0) {
            throw new PolicyDraftValidationError('No register items match the given scope; refine scope facets before generating.');
        }
        const { blocks: sections, providerId, modelId } = await this.composeSections(input.profile_id, items, input.target_maturity_level, input.scope);
        const body_markdown = renderDraftMarkdown(title, input.target_maturity_level, input.scope, items, sections);
        const draft_id = `pd-${cryptoRandom()}`;
        // V3-9-11 / ADR-0025 §6: snapshot the component set the draft is
        // generated against — the latest BOM snapshot per system in the
        // profile. A later warn/fail AI-class delta on one of these marks
        // the draft "drafted against a stale component set".
        const snapshotRefs = (await this.db.query(`SELECT s.system_id AS system_id, s.snapshot_id AS snapshot_id,
              s.canonical_json_sha256 AS canonical_json_sha256
         FROM ai_system_bom_snapshots s
        WHERE s.workspace_id = ?
          AND s.system_id IN (SELECT system_id FROM ai_systems WHERE profile_id = ?)
          AND s.published_at = (
            SELECT MAX(s2.published_at) FROM ai_system_bom_snapshots s2
             WHERE s2.system_id = s.system_id AND s2.workspace_id = ?
          )`, [this.db.workspaceId, input.profile_id, this.db.workspaceId])).results;
        await this.db.execute(`INSERT INTO policy_drafts (
         draft_id, workspace_id, profile_id, title,
         scope_category, scope_classification, scope_framework_id,
         included_item_ids, target_maturity_level,
         body_markdown, original_body_markdown,
         status, source_kind, revision, created_by_user_id,
         provider_id, model_id, component_snapshot_refs_json
       ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [
            draft_id,
            this.db.workspaceId,
            input.profile_id,
            title,
            input.scope.category ?? null,
            input.scope.classification ?? null,
            input.scope.framework_id ?? null,
            JSON.stringify(items.map((i) => i.item_id)),
            input.target_maturity_level,
            body_markdown,
            body_markdown,
            'draft',
            'ai_draft',
            1,
            input.created_by_user_id,
            providerId,
            modelId,
            snapshotRefs.length > 0 ? JSON.stringify(snapshotRefs) : null,
        ]);
        const groundedCount = sections.filter((s) => s.grounded).length;
        return {
            draft_id,
            included_item_ids: items.map((i) => i.item_id),
            section_count: sections.length,
            grounded_section_count: groundedCount,
            template_section_count: sections.length - groundedCount,
        };
    }
    // ------------------------------------------------------------------
    // Update
    // ------------------------------------------------------------------
    async update(draftId, patch) {
        const existing = await this.get(draftId);
        if (!existing)
            return null;
        const sets = [];
        const params = [];
        if (patch.title !== undefined) {
            const t = patch.title.trim();
            if (!t)
                throw new PolicyDraftValidationError('title cannot be empty');
            sets.push('title = ?');
            params.push(t);
        }
        if (patch.body_markdown !== undefined) {
            sets.push('body_markdown = ?');
            params.push(patch.body_markdown);
        }
        if (patch.status !== undefined) {
            sets.push('status = ?');
            params.push(patch.status);
        }
        if (sets.length === 0)
            return existing;
        sets.push(`updated_at = ${sqlNow(this.db)}`);
        params.push(this.db.workspaceId, draftId);
        await this.db.execute(`UPDATE policy_drafts SET ${sets.join(', ')}
        WHERE workspace_id = ? AND draft_id = ?`, params);
        return this.get(draftId);
    }
    async archive(draftId) {
        return this.update(draftId, { status: 'archived' });
    }
    async regenerate(draftId, profileId, opts = {}) {
        const existing = await this.get(draftId);
        if (!existing)
            return null;
        const scope = {
            category: (existing.scope_category ?? undefined),
            classification: (existing.scope_classification ?? undefined),
            framework_id: existing.scope_framework_id ?? undefined,
        };
        const level = opts.new_target_level ?? existing.target_maturity_level;
        const title = (opts.new_title ?? existing.title).trim();
        if (!title)
            throw new PolicyDraftValidationError('title cannot be empty');
        const items = await this.selectItemsForScope(scope);
        if (items.length === 0) {
            throw new PolicyDraftValidationError('No register items match the persisted scope; edit the draft scope before regenerating.');
        }
        const { blocks: sections, providerId, modelId } = await this.composeSections(profileId, items, level, scope);
        const body_markdown = renderDraftMarkdown(title, level, scope, items, sections);
        await this.db.execute(`UPDATE policy_drafts
          SET title = ?,
              target_maturity_level = ?,
              included_item_ids = ?,
              body_markdown = ?,
              original_body_markdown = ?,
              revision = revision + 1,
              provider_id = ?,
              model_id = ?,
              updated_at = ${sqlNow(this.db)}
        WHERE workspace_id = ? AND draft_id = ?`, [
            title,
            level,
            JSON.stringify(items.map((i) => i.item_id)),
            body_markdown,
            body_markdown,
            providerId,
            modelId,
            this.db.workspaceId,
            draftId,
        ]);
        const groundedCount = sections.filter((s) => s.grounded).length;
        return {
            draft_id: draftId,
            included_item_ids: items.map((i) => i.item_id),
            section_count: sections.length,
            grounded_section_count: groundedCount,
            template_section_count: sections.length - groundedCount,
        };
    }
    // ------------------------------------------------------------------
    // Internals — scope selection + section composition
    // ------------------------------------------------------------------
    async selectItemsForScope(scope) {
        const conds = ["status = 'active'"];
        const params = [];
        if (scope.category) {
            conds.push('category = ?');
            params.push(scope.category);
        }
        if (scope.classification) {
            conds.push('classification_applicability LIKE ?');
            params.push(`%"${scope.classification}"%`);
        }
        if (scope.framework_id) {
            conds.push('framework_refs LIKE ?');
            params.push(`%"${scope.framework_id}"%`);
        }
        const res = await this.db.unscoped().query(`SELECT * FROM risk_items
        WHERE ${conds.join(' AND ')}
        ORDER BY category ASC,
                 CASE severity
                   WHEN 'critical' THEN 0
                   WHEN 'high' THEN 1
                   WHEN 'elevated' THEN 2
                   WHEN 'moderate' THEN 3
                   ELSE 4
                 END,
                 item_id`, params);
        return res.results;
    }
    /**
     * Load a per-item evidence bundle for every included item and build
     * a Set of evidence types present anywhere in scope. Used by the
     * preview endpoint (grounded estimate) and by composeSections
     * (grounded flag per section-kind).
     */
    async bundleTypesForScope(profileId, items) {
        const out = new Set();
        const raw = this.db.unscoped();
        for (const item of items) {
            const bundle = await listBindingSummariesForItem(raw, profileId, item.item_id, null);
            for (const b of bundle)
                out.add(b.type);
        }
        return out;
    }
    async composeSections(profileId, items, level, scope) {
        const raw = this.db.unscoped();
        // Load per-item bundles once and pool artifact metadata for the
        // sections that cite artifacts.
        const perItemBundles = new Map();
        const bundleTypes = new Set();
        for (const item of items) {
            const bundle = await listBindingSummariesForItem(raw, profileId, item.item_id, null);
            perItemBundles.set(item.item_id, bundle);
            for (const b of bundle)
                bundleTypes.add(b.type);
        }
        const allArtifactMeta = await this.loadArtifactMetaFromBundles(profileId, perItemBundles);
        // ---- Load additional grounding data sources ----
        // Customer profile — org name, geographies, data types, compliance posture
        const profile = await this.loadProfileForGrounding(profileId);
        // Framework descriptions — issuing body, description, mandatory flag
        const frameworkDetails = await this.loadFrameworkDetailsFor(items);
        // Team composition — roles present in the workspace
        const teamSummary = await this.loadTeamSummary();
        // Assessment summary — readiness scores, gap counts, status distribution
        const assessmentSummary = await this.loadAssessmentSummary(profileId);
        // Build the expanded grounding context.
        const groundingCtx = {
            bundleTypes,
            hasItems: items.length > 0,
            hasProfile: profile !== null,
            hasFrameworkDescriptions: frameworkDetails.size > 0,
            hasAssessment: assessmentSummary !== null,
            hasTeamRoles: teamSummary !== null && teamSummary.total_members > 0,
        };
        const applicableKinds = SECTION_ORDER.filter((k) => LEVEL_GATE[k] <= level);
        // Build the blocks (heading + refs + grounded); bodies fill in below.
        const blocks = applicableKinds.map((kind, idx) => {
            const grounded = isSectionGroundedExpanded(kind, groundingCtx);
            const refs = refsForSection(kind, allArtifactMeta, items);
            return {
                section_kind: kind,
                grounded,
                refs,
                heading: `${idx + 1}. ${SECTION_TITLE[kind]}`,
                body: '',
            };
        });
        // Framework short-name lookup for the register-index appendix. Loaded
        // once; if the master frameworks table doesn't have a row for an id
        // we cite the id verbatim.
        const frameworkNames = await this.loadFrameworkNamesFor(items);
        // Layer 3: load per-item framework mappings (control_ref + control_title)
        // so the LLM can cite specific control IDs and titles in policy clauses.
        const perItemMappings = await this.loadFrameworkMappingsForItems(items);
        // Deterministic sections first — they don't need the LLM at all.
        for (const block of blocks) {
            if (block.section_kind === 'document_control') {
                block.body = renderDocumentControl(level, profile);
            }
            else if (block.section_kind === 'coverage_matrix') {
                block.body = renderCoverageMatrix(items, perItemBundles);
            }
            else if (block.section_kind === 'register_index') {
                block.body = renderRegisterIndex(items, frameworkNames);
            }
        }
        // Placeholder body for everything else — the fallback if the LLM
        // is unavailable or the batch drops.
        for (const block of blocks) {
            if (block.body === '') {
                block.body = renderPlaceholderBody(block, level);
            }
        }
        // LLM fill for non-deterministic sections. providerId/modelId only
        // get set when a batch actually replaces a placeholder body — an
        // extraction call that succeeds but yields nothing usable (or
        // throws) leaves provenance null rather than falsely crediting a
        // provider that contributed no prose to this draft.
        let providerId = null;
        let modelId = null;
        if (this.llm) {
            const llmBlocks = blocks.filter((b) => !DETERMINISTIC_SECTIONS.has(b.section_kind));
            for (let i = 0; i < llmBlocks.length; i += DRAFT_BATCH_SIZE) {
                const batch = llmBlocks.slice(i, i + DRAFT_BATCH_SIZE);
                const filled = await this.fillBatch(batch, items, allArtifactMeta, level, scope, frameworkNames, perItemMappings, profile, frameworkDetails, teamSummary, assessmentSummary);
                if (filled) {
                    modelId = filled.model;
                    providerId = filled.provider;
                }
            }
        }
        return { blocks, providerId, modelId };
    }
    // ---- Additional data loaders for grounding --------------------------------
    async loadProfileForGrounding(profileId) {
        const res = await this.db.unscoped().query('SELECT * FROM customer_profiles WHERE profile_id = ? AND workspace_id = ?', [profileId, this.db.workspaceId]);
        return res.results[0] ?? null;
    }
    async loadFrameworkDetailsFor(items) {
        const ids = new Set();
        for (const item of items) {
            for (const id of safeParseJsonArray(item.framework_refs))
                ids.add(id);
        }
        const out = new Map();
        if (ids.size === 0)
            return out;
        const raw = this.db.unscoped();
        const inFw = sqlIn(ids); // frameworks: control plane (always D1)
        const res = await raw.query(`SELECT framework_id, name, short_name, description, issuing_body,
              version, is_mandatory, url
         FROM frameworks
        WHERE framework_id IN ${inFw.sql}`, inFw.params);
        for (const r of res.results) {
            out.set(r.framework_id, {
                ...r,
                is_mandatory: Boolean(r.is_mandatory),
            });
        }
        return out;
    }
    async loadTeamSummary() {
        try {
            const res = await this.db.query(`SELECT role, COUNT(*) as cnt FROM memberships
          WHERE workspace_id = ? AND removed_at IS NULL
          GROUP BY role`, [this.db.workspaceId]);
            if (res.results.length === 0)
                return null;
            const roles = res.results.map((r) => r.role);
            const total = res.results.reduce((sum, r) => sum + r.cnt, 0);
            return { total_members: total, roles };
        }
        catch {
            return null;
        }
    }
    async loadAssessmentSummary(profileId) {
        try {
            const raw = this.db.unscoped();
            const res = await raw.query(`SELECT aggregate_score, framework_scores, totals
           FROM assessment_scores
          WHERE profile_id = ?
          ORDER BY scored_at DESC LIMIT 1`, [profileId]);
            const row = res.results[0];
            if (!row)
                return null;
            const fwScores = safeParseJson(row.framework_scores, []);
            const totals = safeParseJson(row.totals, {
                applicable: 0, done: 0, todo: 0, in_progress: 0, blocked: 0,
            });
            return {
                aggregate_score: row.aggregate_score,
                framework_scores: fwScores.map((fs) => ({
                    framework_id: fs.framework_id,
                    framework_name: fs.framework_name,
                    score: fs.score,
                    applicable: fs.applicable_items,
                    done: fs.done,
                    todo: fs.todo,
                    in_progress: fs.in_progress,
                })),
                totals,
            };
        }
        catch {
            return null;
        }
    }
    /**
     * Load `frameworks.short_name` (falling back to `name`) for every
     * framework_id cited across the included items. One IN-list read
     * against master data; used by the register-index appendix so the
     * customer sees "NIST AI RMF, ISO 42001" instead of raw slugs.
     */
    async loadFrameworkNamesFor(items) {
        const ids = new Set();
        for (const item of items) {
            for (const id of safeParseJsonArray(item.framework_refs))
                ids.add(id);
        }
        const out = new Map();
        if (ids.size === 0)
            return out;
        const raw = this.db.unscoped();
        const inFw = sqlIn(ids); // frameworks: control plane (always D1)
        const res = await raw.query(`SELECT framework_id, short_name, name
         FROM frameworks
        WHERE framework_id IN ${inFw.sql}`, inFw.params);
        for (const r of res.results) {
            out.set(r.framework_id, r.short_name || r.name);
        }
        return out;
    }
    /**
     * Preload artifact metadata for the set of bindings across the scope.
     * Returns a map of artifact_id → { title, version_label, type,
     * last_attested_at } used by the coverage matrix, related-documents
     * refs, and the LLM prompt payload.
     */
    async loadArtifactMetaFromBundles(profileId, perItemBundles) {
        const bindingIds = new Set();
        for (const bundle of perItemBundles.values()) {
            for (const b of bundle)
                bindingIds.add(b.binding_id);
        }
        const out = new Map();
        if (bindingIds.size === 0)
            return out;
        const raw = this.db.unscoped();
        const idList = Array.from(bindingIds);
        const CHUNK = 90;
        for (let i = 0; i < idList.length; i += CHUNK) {
            const chunk = idList.slice(i, i + CHUNK);
            // d1-in-list: bounded — CHUNK (90) + profile_id = 91 parameters
            const placeholders = chunk.map(() => '?').join(',');
            const res = await raw.query(`SELECT ea.artifact_id, ea.type, ea.title, ea.version_label,
                (SELECT MAX(a.attested_at)
                   FROM evidence_attestations a
                  WHERE a.binding_id = eb.binding_id) AS last_attested_at
           FROM evidence_bindings eb
           JOIN evidence_artifacts ea ON ea.artifact_id = eb.artifact_id
          WHERE eb.profile_id = ?
            AND eb.binding_id IN (${placeholders})`, [profileId, ...chunk]);
            for (const r of res.results) {
                // De-dup by artifact_id — one artifact can back many bindings.
                if (!out.has(r.artifact_id)) {
                    out.set(r.artifact_id, {
                        artifact_id: r.artifact_id,
                        type: r.type,
                        title: r.title,
                        version_label: r.version_label ?? null,
                        last_attested_at: r.last_attested_at ?? null,
                    });
                }
            }
        }
        return out;
    }
    /**
     * Layer 3: load framework_mappings (control_ref + control_title) for
     * every included item. Returns a map of item_id → array of mappings.
     * The LLM uses these to cite specific framework controls in policy
     * clauses (e.g. "AC-2 Account Management", "GV-1.1 Governance").
     */
    async loadFrameworkMappingsForItems(items) {
        const out = new Map();
        if (items.length === 0)
            return out;
        const raw = this.db.unscoped();
        const itemIds = items.map((i) => i.item_id);
        const CHUNK = 90;
        for (let i = 0; i < itemIds.length; i += CHUNK) {
            const chunk = itemIds.slice(i, i + CHUNK);
            // d1-in-list: bounded — CHUNK (90), no other parameters
            const placeholders = chunk.map(() => '?').join(',');
            const res = await raw.query(`SELECT risk_item_id, framework_id, control_ref, control_title, relationship
           FROM framework_mappings
          WHERE risk_item_id IN (${placeholders})`, chunk);
            for (const r of res.results) {
                let arr = out.get(r.risk_item_id);
                if (!arr) {
                    arr = [];
                    out.set(r.risk_item_id, arr);
                }
                arr.push({
                    framework_id: r.framework_id,
                    control_ref: r.control_ref,
                    control_title: r.control_title,
                    relationship: r.relationship,
                });
            }
        }
        return out;
    }
    /**
     * LLM batch fill. Sends a payload of {sections: [...]}, each entry
     * shaped for the drafter contract, and applies the returned bodies
     * back onto the block list.
     */
    async fillBatch(batch, items, artifactMeta, level, scope, frameworkNames, perItemMappings, profile, frameworkDetails, teamSummary, assessmentSummary) {
        if (!this.llm || batch.length === 0)
            return null;
        const scopeSummary = renderScopeSummary(scope, items.length);
        // Layer 2: resolve the category-specific policy context for this scope.
        const categoryContext = resolveCategoryContext(scope, items);
        // Grounding context: customer profile data
        const organizationContext = profile ? {
            organization_name: profile.organization_name,
            geographies: safeParseJsonArray(profile.geographies),
            service_classifications: safeParseJsonArray(profile.service_classifications),
            data_types: safeParseJsonArray(profile.data_types),
            compliance_posture: safeParseJsonArray(profile.compliance_posture).map((id) => frameworkNames.get(id) ?? id),
            industry_codes: safeParseJsonArray(profile.industry_codes),
            customer_types: safeParseJsonArray(profile.customer_types),
        } : null;
        // Grounding context: framework descriptions
        const frameworkContext = Array.from(frameworkDetails.values()).map((fw) => ({
            framework_id: fw.framework_id,
            name: fw.short_name || fw.name,
            full_name: fw.name,
            issuing_body: fw.issuing_body,
            description: fw.description,
            version: fw.version,
            is_mandatory: fw.is_mandatory,
        }));
        // Grounding context: team composition
        const teamContext = teamSummary ? {
            total_members: teamSummary.total_members,
            roles_present: teamSummary.roles,
        } : null;
        // Grounding context: readiness assessment
        const readinessContext = assessmentSummary ? {
            aggregate_score: assessmentSummary.aggregate_score,
            framework_scores: assessmentSummary.framework_scores,
            status_totals: assessmentSummary.totals,
        } : null;
        // Enriched item payload — full description, control_ids, framework
        // mappings, jurisdictions, source info, and effective dates.
        const includedItemsPayload = items.map((i) => {
            const mappings = perItemMappings.get(i.item_id) ?? [];
            return {
                risk_item_id: i.item_id,
                title: i.title,
                description: i.description,
                category: i.category,
                severity: i.severity,
                classifications: safeParseJsonArray(i.classification_applicability),
                frameworks: safeParseJsonArray(i.framework_refs).map((id) => frameworkNames.get(id) ?? id),
                control_ids: safeParseJsonArray(i.control_ids),
                jurisdictions: safeParseJsonArray(i.jurisdiction),
                source_type: i.source_type,
                source_url: i.source_url,
                effective_date: i.effective_date,
                framework_controls: mappings.map((m) => ({
                    framework: frameworkNames.get(m.framework_id) ?? m.framework_id,
                    control_ref: m.control_ref,
                    control_title: m.control_title,
                    relationship: m.relationship,
                })),
                expected_evidence_types: readExpectedEvidenceTypes(i.expected_evidence_types, i.category),
            };
        });
        const payload = batch.map((s) => ({
            section_id: s.section_kind,
            section_kind: s.section_kind,
            section_title: SECTION_TITLE[s.section_kind],
            target_level: level,
            scope_summary: scopeSummary,
            category_policy_context: categoryContext,
            grounded: s.grounded,
            organization: organizationContext,
            frameworks_in_scope: frameworkContext,
            team: teamContext,
            readiness: readinessContext,
            cited_artifacts: s.refs
                .map((id) => artifactMeta.get(id))
                .filter((m) => m !== undefined)
                .map((m) => ({
                type: m.type,
                title: m.title,
                version_label: m.version_label,
                last_attested_at: m.last_attested_at,
            })),
            included_items: includedItemsPayload,
        }));
        let results = [];
        let modelUsed = null;
        let providerUsed;
        // Taint (ADR-0073 §2): profile, team and readiness facts are tenant; register text is reference;
        // cited artifact titles are evidence metadata.
        const from = ['customer_profile.facts', 'register.risk_item_text'];
        if (payload.some((p) => p.cited_artifacts.length > 0))
            from.push('evidence.artifact_text');
        try {
            const extracted = await this.llm.run({
                task: 'policy.draft', template: POLICY_DRAFT_TEMPLATE,
                input: derive('policy_draft.grounding', JSON.stringify({ sections: payload }), from),
                schemaHint: POLICY_DRAFT_SCHEMA,
            });
            if (extracted.status !== 'ok')
                return null; // refused: keep deterministic placeholders
            modelUsed = extracted.model_id ?? null;
            providerUsed = extracted.provider_id;
            if (Array.isArray(extracted.output.results))
                results = extracted.output.results;
        }
        catch {
            return null; // keep placeholders
        }
        const byId = new Map();
        for (const r of results) {
            const id = typeof r.section_id === 'string' ? r.section_id : null;
            const body = typeof r.body === 'string' ? stripFabricatedFencing(r.body) : '';
            if (id && body)
                byId.set(id, body);
        }
        let replacedAny = false;
        for (const s of batch) {
            const body = byId.get(s.section_kind);
            if (body && body.length > 0) {
                s.body = body;
                replacedAny = true;
            }
        }
        return replacedAny ? { model: modelUsed, provider: providerUsed } : null;
    }
}
// ---- LLM prompt + schema -------------------------------------------------
export const DRAFT_BATCH_SIZE = 5;
export const POLICY_DRAFT_INSTRUCTIONS = [
    'You are drafting chapters of a real compliance policy document — the kind a compliance officer hands to auditors and internal reviewers.',
    'This is a STRAWMAN DRAFT, not a generic template. Write substantive, actionable prose that a compliance officer can edit into a final policy with minimal rewriting.',
    'For each input section, write the Markdown BODY of that chapter only — NO heading, NO leading blank line. The renderer supplies the heading.',
    '',
    'Every input carries: the chapter kind, target maturity level, scope summary, category_policy_context (domain-specific guidance for this risk category), included register items (with full descriptions, control IDs, and framework control references), and any workspace artifacts cited by this chapter.',
    '',
    '## Grounding data sources',
    'Each section carries real data from the system. Use ALL of it to write substantive prose — never fall back to generic placeholders when specific data is provided:',
    '- **organization**: the customer\'s organization name, geographies, service classifications, data types handled (PII, PHI, financial, etc.), compliance posture (frameworks being pursued), and industry codes. Use the org name throughout (e.g. "[OrgName] shall..."), name the geographies and data types in the scope section, and reference the compliance posture when naming framework drivers.',
    '- **frameworks_in_scope**: full framework descriptions, issuing bodies, versions, and mandatory/voluntary status. Use the issuing body and description to write authoritative purpose statements and definitions. Cite framework versions (e.g. "NIST AI RMF v1.0") for precision.',
    '- **team**: the workspace\'s team composition — how many members and which roles (owner, admin, editor, viewer, advisor) are present. Map these to the Roles & Responsibilities section rather than using placeholder names.',
    '- **readiness**: the workspace\'s current readiness assessment — aggregate score (0-100), per-framework scores and gap counts, and status totals (how many controls are done, in_progress, todo, blocked). Reference this in the purpose section to state current posture and in monitoring_metrics to set baselines.',
    '- **included_items[*].jurisdictions**: jurisdiction codes (e.g. "us", "eu", "global") — name these in the scope section.',
    '- **included_items[*].source_type**: how the control was sourced (framework, legislation, case_law, breach_intelligence, best_practice) — use in purpose/scope to name the regulatory basis.',
    '- **included_items[*].effective_date**: regulatory effective dates — reference in the scope and policy_statement for compliance timeframes.',
    '',
    '## Chapter shapes',
    '',
    '### purpose',
    'Write 2–3 sentences that a board member can read to understand why this policy exists.',
    'Sentence 1: State the organizational risk this policy mitigates — name the specific risk categories from the included items (e.g. "unauthorized access to AI model training pipelines" not just "access control risks").',
    'Sentence 2: Name the regulatory and framework drivers that mandate this policy — cite the specific frameworks from the included items (e.g. "NIST AI RMF GV-1, ISO 42001 A.2.2, FedRAMP Rev 5 AC-2") not just "applicable frameworks."',
    'Sentence 3: State the compliance outcome — what state the organization is committing to achieve (e.g. "documented, enforced, and periodically attested access controls across all AI systems in scope").',
    '',
    '### scope',
    'Write 2 paragraphs.',
    'Paragraph 1 — Inclusions: Name the AI system classifications this policy applies to (from included_items[*].classifications). State the number of controls covered and reference the severity distribution. Name the specific frameworks that mandate controls in scope (from included_items[*].frameworks). Name the jurisdictions if any appear in the included items.',
    'Paragraph 2 — Exclusions and boundaries: State what is explicitly OUT of scope (e.g. "manual, non-AI processes", "systems classified as low-risk under Article 6 of the EU AI Act", or "third-party hosted models covered by vendor agreements"). Name organizational boundaries: which teams, business units, or geographic locations are covered. End with: "See Appendix B — Register Items in Scope for the full editorial context of each control cited below."',
    '',
    '### definitions',
    'Write a Markdown bullet list of 5–8 key terms. Draw terms from three sources:',
    '1. Domain terms from the category_policy_context (e.g. "privileged access", "least privilege", "segregation of duties" for access_control).',
    '2. AI-specific terms that appear in the included items (e.g. "LLM service", "prompt injection", "model drift", "training data lineage").',
    '3. Compliance/evidence terms the reader needs for this document (e.g. "attestation", "evidence artifact", "maturity level").',
    'Format: **Term** — one-sentence definition drawn from the relevant framework or industry standard. Prefer authoritative definitions (NIST, ISO) over generic ones.',
    '',
    '### policy_statement',
    'This is the heart of the policy. Write numbered Markdown clauses (1., 2., 3., ...).',
    'Group closely related register items into a single clause — do NOT mechanically emit one clause per item. A good clause covers 1–3 related items.',
    'Each clause MUST contain:',
    '(a) The control objective — what the organization commits to (e.g. "The organization shall enforce role-based access control on all AI training environments").',
    '(b) The implementation expectation — HOW compliance is achieved at the target maturity level (e.g. "Access reviews shall be conducted quarterly by the system steward using the organization\'s IAM platform").',
    '(c) The compliance timeframe — when the control must be operational (e.g. "within 90 days of policy approval for existing systems; prior to production deployment for new systems").',
    '(d) The citation — every clause ends with [RR-nnn] for the item(s) it addresses. When citing control_ids, include them parenthetically (e.g. "(AC-2, AC-6)").',
    'Use the category_policy_context to inform the substance of each clause — it contains domain-specific requirements and standard practices for this category.',
    '',
    '### roles_responsibilities',
    'Write a RACI-style table or structured bullet list with 4–6 roles.',
    'For each role, specify:',
    '- The role title (e.g. "Policy Owner", "Control Steward", "System Custodian", "Internal Auditor", "End User").',
    '- The primary accountability — what this role is RESPONSIBLE for (one sentence).',
    '- The escalation path — who this role reports to or escalates issues to.',
    '- The competency requirement — what qualifications or training this role needs (e.g. "annual security awareness training", "CISM or equivalent certification").',
    'Use bracketed placeholders like [name your policy owner] when no cited artifact tells you who fills the role. Use category_policy_context to identify domain-specific roles.',
    '',
    '### procedures',
    'Write numbered steps (1., 2., 3., ...) that describe the operational procedure a person performs to comply with this policy.',
    'Structure as a workflow, not a generic checklist:',
    '- Each step names WHO performs the action, WHAT they do, WHEN they do it, and what EVIDENCE they produce.',
    '- Include decision points: "If [condition], then [action A]; otherwise [action B]."',
    '- Include exception paths: "If the standard procedure cannot be followed, the [role] shall document the exception and obtain approval from [approver] within [timeframe]."',
    '- Include evidence collection touchpoints: "The [role] records the [action] in [system/log] as evidence of compliance."',
    '- Cite [RR-nnn] where a step addresses a specific register item.',
    '- When cited_artifacts include a technical or process artifact, reference it by title and version (e.g. "as documented in Access Review SOP v2.1, last attested 2026-Q2").',
    'Use category_policy_context to guide domain-specific procedural requirements.',
    '',
    '### monitoring_metrics',
    'Write 3–5 metrics organized as a numbered list. Each metric entry contains:',
    '- **Metric name**: a specific, measurable indicator (e.g. "Mean Time to Revoke Access" not just "access review timeliness").',
    '- **Type**: Leading indicator (predictive) or Lagging indicator (outcome-based).',
    '- **Measurement method**: how the metric is collected (automated vs manual, which system, which data source).',
    '- **Target/threshold**: the numeric target or acceptable range (e.g. "< 24 hours", "> 95% coverage", "zero critical findings unresolved > 30 days").',
    '- **Escalation trigger**: what happens when the metric breaches the threshold (e.g. "breaches reported to the AI Governance Council within 48 hours").',
    '- **Review cadence**: who reviews this metric and how often.',
    'When cited_artifacts include a monitoring artifact, reference it by title. Otherwise use a placeholder like "[name your monitoring dashboard here]".',
    '',
    '### enforcement',
    'Write 2 paragraphs.',
    'Paragraph 1 — Violation handling: State how violations are detected (e.g. "through automated monitoring, internal audit findings, or incident reports"), how they are classified (minor, major, critical), and the progressive response for each tier (e.g. "retraining for minor violations; formal corrective action for major; system suspension and executive notification for critical").',
    'Paragraph 2 — Exception governance: State how exceptions are requested (who can request, what justification is required), who approves exceptions (e.g. "the AI Governance Council" or "[name the approving body]"), the maximum duration of an exception, and the re-approval cadence. State that all exceptions are logged and auditable.',
    '',
    '### review_revision',
    'Write 2 paragraphs.',
    'Paragraph 1 — Scheduled review: State the review cadence based on target level (annual at L2, semi-annual at L3, quarterly at L4+). Name the responsible reviewer role and the expected output of each review (e.g. "a written review report documenting any recommended changes, submitted to the policy approver").',
    'Paragraph 2 — Trigger-based review: List specific events that trigger an out-of-cycle review (e.g. "a significant regulatory change affecting the frameworks in scope", "a security incident involving systems covered by this policy", "organizational restructuring that changes the responsible roles", "a material change in the AI systems or data processing activities in scope"). State the change-management discipline: changes are tracked in the document change log, require re-approval by the named approver, and are communicated to all affected roles within [N] business days.',
    '',
    '### related_documents',
    'Write a Markdown bullet list. For each cited_artifact, include: title, version_label (when non-null), last_attested_at (when non-null), and a one-sentence note on how it relates to this policy (e.g. "Defines the technical implementation of access controls referenced in §5 Procedures").',
    'If cited_artifacts is empty, write a structured placeholder list with 4–6 expected related document types: "[Access Control SOP — operational procedures implementing this policy]", "[System Inventory — list of AI systems in scope]", "[Risk Assessment Report — supporting risk analysis]", "[Training Records — evidence of role-based competency]".',
    '',
    '## Voice and citation rules',
    'Voice: matter-of-fact, third-person, no marketing language, no first-person plural ("we", "our"). Address the reader as "the organization" or "the workspace owner".',
    'Write substantively — every sentence should contain a specific commitment, requirement, or measurable criterion. Avoid vague language like "appropriate measures", "as needed", "periodically" — replace with concrete actions, named roles, and defined cadences.',
    'When grounded=true, cite the cited_artifacts by their EXACT title and version_label — never invent titles, versions, dashboard names, dates, or personnel.',
    'When grounded=false, use bracketed placeholders like [name your DLP dashboard here] for any specific detail you do not have. Placeholders should describe WHAT goes there (e.g. "[name the quarterly access review process owner]" not just "[name]").',
    'Return one entry per input in the results array; use the exact section_id as the join key. Do NOT include the Markdown heading (## ...) in your body — the renderer inserts it.',
].join('\n');
/** ADR-0073: reviewed static template; the grounded section payload is the classified input. */
export const POLICY_DRAFT_TEMPLATE = defineTemplate('policy.draft', 'v1', POLICY_DRAFT_INSTRUCTIONS, []);
export const POLICY_DRAFT_SCHEMA = {
    results: [
        {
            section_id: 'string (matches an input section_id verbatim)',
            body: 'string (Markdown body without a heading; may contain bullet lists, numbered lists, or paragraphs per the chapter shape)',
        },
    ],
};
// ---- Deterministic section renderers -------------------------------------
function renderDocumentControl(level, profile) {
    const cadence = level >= 4 ? 'Quarterly' : level === 3 ? 'Semi-annual' : 'Annual';
    const orgName = profile?.organization_name
        ? profile.organization_name
        : '[Organization name]';
    return [
        '| Field | Value |',
        '|---|---|',
        `| Organization | ${orgName} |`,
        '| Owner | [name a responsible role or individual] |',
        '| Version | 0.1 (draft) |',
        '| Effective date | [YYYY-MM-DD] |',
        `| Review cadence | ${cadence} |`,
        '| Next review | [YYYY-MM-DD] |',
        '| Approver | [name the approving body — e.g. AI Governance Council] |',
        '| Classification | Internal |',
    ].join('\n');
}
function renderCoverageMatrix(items, perItemBundles) {
    const lines = [
        'Register-item coverage for this policy. Evidence-status glyphs reflect the workspace bundle at generation time.',
        '',
        '| Register item | Category | Severity | Policy | Technical | Process | Monitoring |',
        '|---|---|---|:---:|:---:|:---:|:---:|',
    ];
    const glyph = (present) => (present ? '✓' : '—');
    for (const item of items) {
        const bundle = perItemBundles.get(item.item_id) ?? [];
        const has = (t) => bundle.some((b) => b.type === t);
        lines.push(`| ${item.item_id} — ${escapePipe(item.title)}` +
            ` | ${item.category}` +
            ` | ${item.severity}` +
            ` | ${glyph(has('policy'))}` +
            ` | ${glyph(has('technical'))}` +
            ` | ${glyph(has('process'))}` +
            ` | ${glyph(has('monitoring'))} |`);
    }
    return lines.join('\n');
}
function escapePipe(s) {
    return s.replace(/\|/g, '\\|');
}
function refsForSection(kind, allArtifactMeta, items) {
    if (kind === 'coverage_matrix' || kind === 'register_index') {
        return items.map((i) => i.item_id);
    }
    const evTypes = SECTION_EVIDENCE_TYPES[kind];
    if (evTypes.length === 0)
        return [];
    const out = [];
    for (const meta of allArtifactMeta.values()) {
        if (evTypes.includes(meta.type))
            out.push(meta.artifact_id);
    }
    return out;
}
/**
 * Appendix B — Register Items in Scope.
 *
 * Deterministic — the copy is the register's own editorial prose plus
 * the framework short-names + classification-applicability list. Read by
 * the human filling in the policy so they know what each cited [RR-nnn]
 * clause is really about, which frameworks mandate it, and where the
 * risk was first observed (breach case, court ruling, framework
 * publication).
 */
function renderRegisterIndex(items, frameworkNames) {
    const lines = [
        'Editorial context for every register item cited by this policy. Use this appendix when refining the Policy Statement clauses so each [RR-nnn] rewrite stays anchored to the underlying obligation and its source.',
        '',
    ];
    for (const item of items) {
        const frameworks = safeParseJsonArray(item.framework_refs)
            .map((id) => frameworkNames.get(id) ?? id);
        const classifications = safeParseJsonArray(item.classification_applicability);
        const evidenceTypes = readExpectedEvidenceTypes(item.expected_evidence_types, item.category);
        lines.push(`### ${item.item_id} — ${item.title}`);
        lines.push('');
        const metaBits = [
            `**Category:** ${item.category}`,
            `**Severity:** ${item.severity}`,
            `**Source:** ${item.source_type.replace(/_/g, ' ')}`,
        ];
        if (frameworks.length > 0) {
            metaBits.push(`**Frameworks:** ${frameworks.join(', ')}`);
        }
        if (classifications.length > 0) {
            metaBits.push(`**Applies to:** ${classifications.join(', ')}`);
        }
        lines.push(metaBits.join(' · '));
        lines.push('');
        lines.push(item.description.trim());
        lines.push('');
        lines.push(`**Suggested evidence:** ${evidenceTypes.join(' + ')}`);
        if (item.source_url) {
            lines.push(`**Reference:** ${item.source_url}`);
        }
        if (item.effective_date) {
            lines.push(`**Effective:** ${item.effective_date}`);
        }
        lines.push('');
    }
    return lines.join('\n').trim();
}
function renderPlaceholderBody(section, level) {
    const cadenceMonths = CADENCE_MONTHS_BY_LEVEL[level];
    const cadenceLabel = level >= 4 ? 'quarterly' : level === 3 ? 'semi-annual' : 'annual';
    const prompts = {
        document_control: '',
        purpose: [
            '[State the organizational risk this policy mitigates — name the specific risk categories and the most severe controls in scope.]',
            '',
            '[Name the regulatory and framework drivers that mandate this policy — cite specific frameworks such as NIST AI RMF, ISO 42001, FedRAMP, or EU AI Act articles.]',
            '',
            '[State the compliance outcome — what state the organization is committing to achieve, e.g. "documented, enforced, and periodically attested controls across all AI systems in scope."]',
        ].join('\n'),
        scope: [
            '**Inclusions:** [Name the AI system classifications this policy applies to. State the number of controls covered, the severity distribution, the frameworks that mandate them, and any jurisdictional applicability.]',
            '',
            '**Exclusions and boundaries:** [State what is explicitly out of scope — e.g. manual non-AI processes, low-risk systems, third-party hosted models covered by vendor agreements. Name organizational boundaries: teams, business units, or geographies covered.] See Appendix B — Register Items in Scope for the full editorial context of each control cited below.',
        ].join('\n'),
        definitions: [
            '- **[Domain term from this risk category]** — [One-sentence definition from the relevant framework or industry standard, e.g. NIST, ISO.]',
            '- **[AI-specific term from the register items]** — [One-sentence definition.]',
            '- **[Compliance/evidence term]** — [One-sentence definition.]',
            '- **[Additional term]** — [One-sentence definition.]',
            '- **[Additional term]** — [One-sentence definition.]',
        ].join('\n'),
        policy_statement: [
            '1. [Control objective: what the organization commits to.] [Implementation expectation: how compliance is achieved at the target maturity level.] [Compliance timeframe: when the control must be operational.] [RR-nnn] (control-ids)',
            '2. [Group related register items into a single clause. State the commitment, the implementation approach, and the timeframe.] [RR-nnn, RR-nnn] (control-ids)',
            '3. [Continue for each control objective or group of closely related items.] [RR-nnn]',
        ].join('\n'),
        roles_responsibilities: [
            '- **Policy Owner** — [Name or placeholder]. Accountable for policy currency, effectiveness reviews, and exception approvals. Reports to [name the governance body]. Required competency: [qualification or training requirement].',
            '- **Control Steward** — [Name or placeholder]. Responsible for implementing and maintaining the controls defined in this policy. Escalates to the Policy Owner. Required competency: [qualification].',
            '- **System Custodian** — [Name or placeholder]. Responsible for the technical operation of AI systems in scope. Reports compliance status to the Control Steward.',
            '- **Internal Auditor** — [Name or placeholder]. Conducts periodic compliance assessments and reports findings to [governance body].',
            '- **End Users** — All workforce members interacting with the AI systems in scope. Required: [annual security/AI awareness training].',
        ].join('\n'),
        procedures: [
            `1. **[Who]** performs [what action] [when/how often] and produces [what evidence]. [RR-nnn]`,
            `2. **[Who]** reviews [what] using [system/tool]. If [condition], then [action A]; otherwise [action B].`,
            `3. **[Who]** records [action] in [system/log] as evidence of compliance.`,
            `4. **Exception path:** If the standard procedure cannot be followed, the [role] documents the exception and obtains approval from [approver] within [timeframe].`,
            '',
            `Suggested attestation cadence at L${level}: every ${cadenceMonths} months.`,
        ].join('\n'),
        monitoring_metrics: [
            '1. **[Metric name]** (Leading indicator) — [Measurement method: automated/manual, which system]. Target: [numeric threshold]. Escalation: [what happens on breach]. Reviewed [cadence] by [role].',
            '2. **[Metric name]** (Lagging indicator) — [Measurement method]. Target: [threshold]. Escalation: [action]. Reviewed [cadence] by [role].',
            '3. **[Metric name]** — [Measurement method]. Target: [threshold]. Escalation: [action]. Reviewed [cadence] by [role].',
        ].join('\n'),
        enforcement: [
            '**Violation handling:** Violations are detected through [automated monitoring, internal audit findings, or incident reports] and classified as minor, major, or critical. Minor violations result in [retraining]. Major violations result in [formal corrective action]. Critical violations result in [system suspension and executive notification].',
            '',
            '**Exception governance:** Exceptions are requested by [role], require [written justification], and are approved by [the AI Governance Council or name the approving body]. Maximum exception duration: [timeframe]. Re-approval cadence: [cadence]. All exceptions are logged and auditable.',
        ].join('\n'),
        review_revision: [
            `**Scheduled review:** This policy is reviewed on a ${cadenceLabel} cadence by [name the reviewer role]. Each review produces a written report documenting recommended changes, submitted to [the policy approver].`,
            '',
            `**Trigger-based review:** An out-of-cycle review is triggered by: (a) a significant regulatory change affecting frameworks in scope, (b) a security incident involving systems covered by this policy, (c) organizational restructuring that changes responsible roles, or (d) a material change in the AI systems or data processing activities in scope. Changes are tracked in the document change log, require re-approval by the named approver, and are communicated to affected roles within [N] business days.`,
        ].join('\n'),
        related_documents: [
            '- [Operational SOP — procedures implementing the controls in this policy]',
            '- [System Inventory — list of AI systems in scope]',
            '- [Risk Assessment Report — supporting risk analysis for this category]',
            '- [Training Records — evidence of role-based competency requirements]',
            '- [Incident Response Plan — referenced by the enforcement section]',
            '- [Monitoring Dashboard — referenced by the metrics section]',
        ].join('\n'),
        coverage_matrix: '',
        register_index: '',
    };
    return prompts[section.section_kind] ?? '';
}
// ---- Utility helpers -----------------------------------------------------
function renderScopeSummary(scope, itemCount) {
    const parts = [];
    if (scope.category)
        parts.push(`category=${scope.category}`);
    if (scope.classification)
        parts.push(`classification=${scope.classification}`);
    if (scope.framework_id)
        parts.push(`framework=${scope.framework_id}`);
    const scopeText = parts.length ? parts.join(', ') : 'all active register items';
    return `${scopeText}; ${itemCount} register item${itemCount === 1 ? '' : 's'} in scope`;
}
function resolveCategoryContext(scope, items) {
    if (scope.category) {
        return CATEGORY_POLICY_CONTEXT[scope.category];
    }
    const categories = new Set(items.map((i) => i.category));
    if (categories.size === 1) {
        const cat = categories.values().next().value;
        return CATEGORY_POLICY_CONTEXT[cat];
    }
    const summaries = Array.from(categories)
        .slice(0, 4)
        .map((c) => `[${c}] ${CATEGORY_POLICY_CONTEXT[c]}`);
    if (categories.size > 4) {
        summaries.push(`...and ${categories.size - 4} more categories.`);
    }
    return summaries.join('\n\n');
}
function stripFabricatedFencing(raw) {
    return raw
        .replace(/^\s*```(?:markdown|md)?\s*/i, '')
        .replace(/```\s*$/i, '')
        .trim();
}
function truncate(s, max) {
    if (!s)
        return '';
    return s.length <= max ? s : `${s.slice(0, max)}…`;
}
// ---- Markdown rendering --------------------------------------------------
/**
 * Compose the full Markdown body: title + one-line policy metadata +
 * numbered chapters. Wire-format comment sits above each H2 so the
 * renderer + export can look up chip state and refs without re-parsing
 * the body.
 */
export function renderDraftMarkdown(title, level, scope, items, sections) {
    const summary = renderScopeSummary(scope, items.length);
    const preamble = [
        `# ${title}`,
        '',
        `> **Draft policy — target maturity L${level}.** Scope: ${summary}.`,
        `> AI-drafted skeleton. Every chapter below carries a Grounded or Template chip; Template chapters are boilerplate the workspace owner must fill in before this document is treated as evidence.`,
        '',
        '---',
        '',
    ].join('\n');
    const rendered = sections.map(renderSection).join('\n\n');
    return `${preamble}${rendered}\n`;
}
export function renderSection(section) {
    const comment = [
        '<!-- vexbo:section',
        `kind=${section.section_kind}`,
        `grounded=${section.grounded ? 'true' : 'false'}`,
        `refs=${JSON.stringify(section.refs)}`,
        '-->',
    ].join(' ');
    const body = section.body.trim();
    return `${comment}\n## ${section.heading}\n\n${body}`;
}
const SECTION_COMMENT_RE = /<!--\s*vexbo:section\s+([^]*?)-->/g;
const KNOWN_KINDS = new Set(SECTION_ORDER);
/**
 * Parse a body_markdown string into its section blocks. Preserves the
 * H2 heading and body prose. Only accepts the current wire format —
 * legacy drafts (pre-reshape) fall back to a permissive parse and
 * render as one big "unparsed body" block; those drafts should be
 * regenerated or archived.
 */
export function parseDraftSections(bodyMarkdown) {
    const blocks = [];
    const matches = [];
    for (const m of bodyMarkdown.matchAll(SECTION_COMMENT_RE)) {
        if (m.index === undefined)
            continue;
        matches.push({
            index: m.index,
            end: m.index + m[0].length,
            attrs: m[1] ?? '',
        });
    }
    for (let i = 0; i < matches.length; i++) {
        const cur = matches[i];
        const next = matches[i + 1];
        const chunk = bodyMarkdown
            .slice(cur.end, next ? next.index : undefined)
            .trim();
        const nl = chunk.indexOf('\n');
        const headingLine = nl === -1 ? chunk : chunk.slice(0, nl);
        const body = nl === -1 ? '' : chunk.slice(nl + 1).trim();
        const heading = headingLine.replace(/^##\s*/, '').trim();
        const attrs = parseAttrs(cur.attrs);
        // `kind` is the current attribute; fall back to attrs.type for
        // drafts written by the initial coverage-matrix release. Those old
        // drafts render as best-effort — the customer should regenerate
        // in the policy shape.
        const kindRaw = attrs['kind'] ?? attrs['type'] ?? '';
        const kind = (KNOWN_KINDS.has(kindRaw)
            ? kindRaw
            : 'policy_statement');
        blocks.push({
            section_kind: kind,
            grounded: attrs['grounded'] === 'true',
            refs: safeParseJsonArray(attrs['refs'] ?? '[]'),
            heading,
            body,
        });
    }
    return blocks;
}
function parseAttrs(raw) {
    const out = {};
    let i = 0;
    while (i < raw.length) {
        while (i < raw.length && /\s/.test(raw[i]))
            i++;
        if (i >= raw.length)
            break;
        const eq = raw.indexOf('=', i);
        if (eq === -1)
            break;
        const key = raw.slice(i, eq).trim();
        let j = eq + 1;
        let value;
        if (raw[j] === '[') {
            let depth = 0;
            const start = j;
            while (j < raw.length) {
                const ch = raw[j];
                if (ch === '[')
                    depth++;
                else if (ch === ']') {
                    depth--;
                    if (depth === 0) {
                        j++;
                        break;
                    }
                }
                j++;
            }
            value = raw.slice(start, j);
        }
        else {
            const wsIdx = raw.slice(j).search(/\s/);
            const end = wsIdx === -1 ? raw.length : j + wsIdx;
            value = raw.slice(j, end);
            j = end;
        }
        out[key] = value;
        i = j;
    }
    return out;
}
// ---- M7 export helpers ---------------------------------------------------
/**
 * Strip `<!-- vexbo:section ... -->` comments from a draft body,
 * leaving the H2 headings + paragraphs. Used by the Markdown export.
 */
export function stripSectionComments(bodyMarkdown) {
    return (bodyMarkdown
        .replace(SECTION_COMMENT_RE, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim() + '\n');
}
/**
 * Minimal Markdown → HTML for the printable export. Handles H1/H2/H3
 * headings, blockquotes, horizontal rules, GitHub-flavored tables,
 * ordered/unordered lists, and inline emphasis. Not a full CommonMark
 * parser — sufficient for the drafter's output shape and stays
 * dependency-free for the worker.
 */
export function renderDraftHtml(title, bodyMarkdown) {
    const body = renderMarkdownFragment(stripSectionComments(bodyMarkdown));
    return htmlDocument(title, body);
}
/**
 * Convert a Markdown fragment (no section-comment stripping, no
 * <html>/<body> wrapper) to HTML. Used by the SPA to render one
 * section's body inline while keeping the C15 chip overlay above it.
 * Kept as a separate export so callers can compose their own layout.
 */
export function renderMarkdownFragment(bodyMarkdown) {
    const escaped = bodyMarkdown
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    const lines = escaped.split('\n');
    const out = [];
    let list = null;
    let inParagraph = false;
    let inTable = false;
    let tableRows = [];
    const closeList = () => {
        if (list) {
            out.push(list.kind === 'ul' ? '</ul>' : '</ol>');
            list = null;
        }
    };
    const closeParagraph = () => {
        if (inParagraph) {
            out.push('</p>');
            inParagraph = false;
        }
    };
    const flushTable = () => {
        if (!inTable)
            return;
        // First row = header, second = alignment (ignored), rest = body.
        if (tableRows.length >= 2) {
            const header = splitTableRow(tableRows[0]);
            const bodyRows = tableRows.slice(2).map(splitTableRow);
            out.push('<table>');
            out.push('<thead><tr>' + header.map((c) => `<th>${inlineFormat(c)}</th>`).join('') + '</tr></thead>');
            out.push('<tbody>');
            for (const row of bodyRows) {
                out.push('<tr>' + row.map((c) => `<td>${inlineFormat(c)}</td>`).join('') + '</tr>');
            }
            out.push('</tbody></table>');
        }
        tableRows = [];
        inTable = false;
    };
    const closeBlocks = () => {
        closeList();
        closeParagraph();
        flushTable();
    };
    for (const rawLine of lines) {
        const line = rawLine.trimEnd();
        // Table detection: a line starting with `|` and containing another `|`.
        if (line.trimStart().startsWith('|') && line.includes('|', line.indexOf('|') + 1)) {
            closeList();
            closeParagraph();
            inTable = true;
            tableRows.push(line);
            continue;
        }
        else if (inTable) {
            flushTable();
        }
        if (line === '') {
            closeBlocks();
            continue;
        }
        if (line.startsWith('# ')) {
            closeBlocks();
            out.push(`<h1>${inlineFormat(line.slice(2))}</h1>`);
            continue;
        }
        if (line.startsWith('## ')) {
            closeBlocks();
            out.push(`<h2>${inlineFormat(line.slice(3))}</h2>`);
            continue;
        }
        if (line.startsWith('### ')) {
            closeBlocks();
            out.push(`<h3>${inlineFormat(line.slice(4))}</h3>`);
            continue;
        }
        if (line.startsWith('&gt; ')) {
            // blockquote (was `> ` before HTML-escape)
            closeBlocks();
            out.push(`<blockquote>${inlineFormat(line.slice(5))}</blockquote>`);
            continue;
        }
        if (line === '---' || line === '***') {
            closeBlocks();
            out.push('<hr />');
            continue;
        }
        // Unordered list item: `- text`
        if (/^\s*-\s+/.test(line)) {
            closeParagraph();
            if (list?.kind !== 'ul') {
                closeList();
                out.push('<ul>');
                list = { kind: 'ul' };
            }
            out.push(`<li>${inlineFormat(line.replace(/^\s*-\s+/, ''))}</li>`);
            continue;
        }
        // Ordered list item: `1. text`
        if (/^\s*\d+\.\s+/.test(line)) {
            closeParagraph();
            if (list?.kind !== 'ol') {
                closeList();
                out.push('<ol>');
                list = { kind: 'ol' };
            }
            out.push(`<li>${inlineFormat(line.replace(/^\s*\d+\.\s+/, ''))}</li>`);
            continue;
        }
        // Paragraph line.
        closeList();
        if (!inParagraph) {
            out.push('<p>');
            inParagraph = true;
        }
        else {
            out.push('<br />');
        }
        out.push(inlineFormat(line));
    }
    closeBlocks();
    return out.join('\n');
}
function splitTableRow(line) {
    return line
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((c) => c.trim());
}
function inlineFormat(s) {
    return s
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
        .replace(/`([^`\n]+)`/g, '<code>$1</code>');
}
function htmlDocument(title, body) {
    const titleEsc = title
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${titleEsc}</title>
<style>
  body { font-family: Georgia, 'Times New Roman', serif; color: #111; max-width: 780px; margin: 2.5rem auto; padding: 0 1.5rem; line-height: 1.55; }
  h1 { font-size: 1.9rem; margin-top: 0; }
  h2 { font-size: 1.35rem; margin-top: 2rem; border-bottom: 1px solid #ccc; padding-bottom: 0.25rem; }
  h3 { font-size: 1.1rem; margin-top: 1.5rem; }
  p { margin: 0.75rem 0; }
  ul, ol { margin: 0.75rem 0 0.75rem 1.5rem; }
  li { margin: 0.25rem 0; }
  blockquote { border-left: 3px solid #ccc; margin: 1rem 0; padding: 0.25rem 1rem; color: #555; }
  hr { border: none; border-top: 1px solid #ddd; margin: 2rem 0; }
  code { background: #f3f3f3; padding: 1px 4px; border-radius: 3px; font-family: 'SF Mono', Menlo, monospace; font-size: 0.9em; }
  table { border-collapse: collapse; margin: 1rem 0; width: 100%; font-size: 0.95rem; }
  th, td { border: 1px solid #ccc; padding: 0.4rem 0.6rem; text-align: left; vertical-align: top; }
  th { background: #f6f6f6; }
  @media print { body { margin: 0; padding: 1cm; } }
</style>
</head>
<body>
${body}
</body>
</html>
`;
}
function safeParseJsonArray(raw) {
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed)
            ? parsed.filter((v) => typeof v === 'string')
            : [];
    }
    catch {
        return [];
    }
}
function safeParseJson(raw, fallback) {
    try {
        return JSON.parse(raw);
    }
    catch {
        return fallback;
    }
}
// ---- Profile-id resolution helper ---------------------------------------
export async function resolveProfileIdForWorkspace(raw, workspaceId) {
    const res = await raw.query(`SELECT profile_id FROM customer_profiles
      WHERE workspace_id = ?
      ORDER BY created_at
      LIMIT 1`, [workspaceId]);
    return res.results[0]?.profile_id ?? null;
}
// ---- Re-exports for consumers -------------------------------------------
export { SECTION_ORDER as POLICY_DRAFT_SECTION_ORDER, SECTION_TITLE as POLICY_DRAFT_SECTION_TITLE, LEVEL_GATE as POLICY_DRAFT_LEVEL_GATE, CADENCE_MONTHS_BY_LEVEL as POLICY_DRAFT_CADENCE_MONTHS_BY_LEVEL, };
//# sourceMappingURL=policy-drafter-service.js.map